import { gql } from "@apollo/client";

/**
 * Writes one setting row.
 *
 * The server does NOT validate `value`: it returns `true` for unparseable
 * input, stores it, and then silently keeps applying the previous limit. So
 * every caller must run `validateLimitWindows` first — see rateLimit.ts.
 */
export const UPDATE_RATE_LIMIT_SETTING = gql`
  mutation UpdateRateLimitSetting($key: String!, $value: String!) {
    updateRateLimitSetting(key: $key, value: $value)
  }
`;

/**
 * Step 2 of the unblock flow, and the primary way to unblock anyone.
 *
 * Resolves the identifier through the OTP send history, so it clears the whole
 * member — every destination, IP, device and account correlated with it —
 * rather than the one key the admin happened to type.
 *
 * Two fields in the result are load-bearing and must reach the screen:
 *
 * - `countersCleared` is Redis keys, not requests. See `UnblockOtpResult`.
 * - `skippedDestinations` is populated when the server refused to cascade
 *   (`cascadeWouldBeRefused` on the status query predicts this). Those members
 *   are still blocked and can only be cleared one at a time. Dropping the list
 *   silently strands them, which is the entire reason the refusal reports them.
 */
export const UNBLOCK_OTP_RECIPIENT = gql`
  mutation UnblockOtpRecipient($identifier: String!) {
    unblockOtpRecipient(identifier: $identifier) {
      kind
      countersCleared
      cascaded
      destinationsCleared
      ipAddressesCleared
      devicesCleared
      accountsCleared
      skippedDestinations
      registrationBackstopCleared
    }
  }
`;

/**
 * Escape hatch, demoted behind an "Advanced" disclosure — prefer
 * `UNBLOCK_OTP_RECIPIENT` for anything that went through the OTP path.
 *
 * Still the only way to clear a key that has no OTP send history behind it to
 * resolve: a `verificationSid` arg counter, or an IP with no `OtpSendLog`
 * rows. It matches on the literal string only, so it clears exactly the keys
 * containing that value and nothing correlated with them.
 *
 * Unblocks one IP or phone number: clears its Redis counters *and* releases the
 * durable registration block held in Postgres. (Before the backend fix, only
 * Redis was cleared, so a capped IP stayed blocked for the rest of the window
 * however many times the button was pressed.)
 *
 * The `Int` it returns is **counter keys deleted, not registrations or
 * attempts** — one key exists per window per tier, and the global envelope
 * alone holds two, so it runs several times higher than any count an admin
 * would expect. Never present it as an attempt count, and never treat 0 as a
 * failure: it just means nothing was actively counting. The unblock still
 * happened.
 *
 * Phone numbers must carry a country code (+15551234567) or nothing matches.
 */
export const RESET_RATE_LIMIT = gql`
  mutation ResetRateLimit($dimensionValue: String!) {
    resetRateLimit(dimensionValue: $dimensionValue)
  }
`;
